import { useCallback, useState } from "react";

import { Search } from "@/design-system/components/Search/Search";
import { toast } from "@/design-system/components/Toast/Toast";

import { Avatar, ResolvedUser } from "@/components/users/Avatar";

import { makeRequest } from "@/util";

export type MasqueradeFilterKind =
  | "users"
  | "accounts"
  | "both"
  | "environments";

type MasqueradeOptionUser = {
  kind: "user";
  accountID: string;
  accountName?: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  avatar?: string | null;
};

type MasqueradeOptionAccount = {
  kind: "account";
  accountID: string;
  accountName?: string;
};

type MasqueradeOptionEnvironment = {
  kind: "environment";
  envID: string;
  accountID: string;
  accountName?: string;
  projectName?: string;
  envName?: string;
};

export type MasqueradeOption =
  | MasqueradeOptionUser
  | MasqueradeOptionAccount
  | MasqueradeOptionEnvironment;

type MasqueradeResponse = { data: MasqueradeOption[] };

type AutocompleteMasqueradeProps = {
  value: string;
  onChange: (value: string) => void;
  kind: MasqueradeFilterKind;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange">;

const AUTOCOMPLETE_DEBOUNCE_MS = 200;

function userResolved(opt: MasqueradeOptionUser): ResolvedUser {
  return {
    email: opt.email,
    first_name: opt.firstName ?? undefined,
    last_name: opt.lastName ?? undefined,
    avatar: opt.avatar ?? undefined,
  };
}

function userFullName(opt: MasqueradeOptionUser): string | null {
  const parts = [opt.firstName, opt.lastName].filter(
    (p): p is string => !!p && p.trim().length > 0,
  );
  if (parts.length === 0) return null;
  return parts.join(" ").trim();
}

function optionLabel(option: MasqueradeOption): string {
  if (option.kind === "environment") return option.envID;
  if (option.kind === "account") return option.accountID;
  return option.email;
}

function optionKey(option: MasqueradeOption): string {
  return `${option.kind}:${option.accountID}:${optionLabel(option)}`;
}

function OptionRow({ option }: { option: MasqueradeOption }) {
  if (option.kind === "environment") {
    return (
      <div className="flex w-full flex-col">
        <div className="flex items-center gap-2">
          <span className="bg-surface-subtle text-textSubtle rounded px-1.5 py-0.5 text-xs font-medium uppercase">
            Env
          </span>
          <span className="text-textHighEmphasis font-medium">
            {[option.projectName, option.envName].filter(Boolean).join(" / ")}
          </span>
        </div>
        <span className="text-textSubtle text-xs">
          {option.accountName || "(Unknown)"}
        </span>
        <span className="text-textSubtle font-mono text-xs">
          {option.envID}
        </span>
      </div>
    );
  }

  if (option.kind === "account") {
    return (
      <div className="flex w-full flex-col">
        <div className="flex items-center gap-2">
          <span className="bg-surface-subtle text-textSubtle rounded px-1.5 py-0.5 text-xs font-medium uppercase">
            Account
          </span>
          <span className="text-textHighEmphasis font-medium">
            {option.accountName || "(Unknown)"}
          </span>
        </div>
        <span className="text-textSubtle font-mono text-xs">
          {option.accountID}
        </span>
      </div>
    );
  }

  const fullName = userFullName(option);

  return (
    <div className="flex w-full items-center gap-3">
      <Avatar user={userResolved(option)} size="sm" />
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-2">
          <span className="bg-surface-subtle text-textSubtle rounded px-1.5 py-0.5 text-xs font-medium uppercase">
            User
          </span>
          <span className="text-textHighEmphasis truncate font-medium">
            {fullName ?? option.email}
          </span>
        </div>
        <span className="text-textSubtle truncate text-xs">
          {fullName ? option.email : null}
          {fullName && option.accountName ? " · " : null}
          {option.accountName}
        </span>
      </div>
    </div>
  );
}

export const AutocompleteMasquerade = ({
  value,
  onChange,
  kind,
  ...rest
}: AutocompleteMasqueradeProps) => {
  // The parent receives trimmed values, so the raw text is kept here to let a
  // typed space survive until the next character.
  const [inputText, setInputText] = useState(value);
  const displayedText = inputText.trim() === value ? inputText : value;

  const getResults = useCallback(
    async (query: string) => {
      try {
        const response = (await makeRequest("autocomplete_masquerade", {
          masquerade_value: query,
          kind,
        })) as MasqueradeResponse;
        return response.data ?? [];
      } catch (error) {
        console.error(error);
        toast.error("Something went wrong");
        return [];
      }
    },
    [kind],
  );

  return (
    <Search
      externalQuery={value}
      getResults={getResults}
      formatResult={(option) => (
        <div className="py-1">
          <OptionRow option={option} />
        </div>
      )}
      getId={optionKey}
      debounceTime={AUTOCOMPLETE_DEBOUNCE_MS}
      onSelect={(option) => {
        const label = optionLabel(option);
        setInputText(label);
        onChange(label);
      }}
    >
      <input
        {...rest}
        autoComplete="off"
        value={displayedText}
        onChange={(e) => {
          setInputText(e.target.value);
          onChange(e.target.value.trim());
        }}
      />
    </Search>
  );
};
